import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Routes, Route } from 'react-router-dom';
import { withRouterLegacy } from 'core/routerUtils';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as roomActions from 'features/room-builder/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as materialChainActions from 'features/material-chain/duck';
import { RoomInformation, RoomStructure, RoomSeatingDetails } from 'features/room-builder/';
import { AlertMessage, PageNotFound, Spinner } from 'shared/components/';
import { ROOM_STATUS } from 'shared/constants';
import { mapRoomTypesById } from 'shared/selectors';
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import 'features/room-builder/style.scss';
import './EventRoomsContainer.scss';

const getUrl = (stepUrl, eventId, roomId) =>
  `/test-event/${eventId}/rooms${stepUrl}${roomId ? `/${roomId}` : ''}`;

const defaultRoom = {
  name: '',
  locationEvent: {},
  capacity: '',
  timing: '',
  roomFacilities: [],
  shape: null,
  seating: null,
  deskType: null,
  supervisorDesk: null,
  status: 'inactive',
  seatingRowCount: 0,
  seatingColumnCount: 0,
  writingSurfaceWidth: 0,
  writingSurfaceDepth: 0,
  distanceBetweenShoulders: 0,
  distanceBetweenHeads: 0,
  singleRoomFlag: false,
};

const getLocationEventCapacity = (location, timingType) => {
  let sum = 0;
  if (location.locationEvent?.locationEventCapacities.length) {
    location.locationEvent.locationEventCapacities.forEach(
      (exam) => (sum += exam.capacityType.timing === timingType ? exam.capacity : 0)
    );
  }
  return sum;
};

const mapRooms = (locations, rooms) =>
  locations.map((location) => {
    const foundRooms = rooms.filter((room) => room.locationEvent.id === location.locationEvent.id);
    location.roomCapacity = 0;
    location.capacityStandard = getLocationEventCapacity(location, 'standard');
    location.capacityExtended = getLocationEventCapacity(location, 'extended');

    if (!foundRooms.length) {
      location.rooms = foundRooms;
      return location;
    }

    foundRooms.forEach((room) => (location.roomCapacity += room.capacity));
    location.rooms = foundRooms;
    return location;
  });

class EventRoomsContainer extends Component {
  constructor(props) {
    super(props);

    const { params } = this.props.match;
    let step = this.BUILD_STEPS.findIndex((bStep) => bStep.url === `/${params.step}`);

    if (step === -1) {
      step = 0;
    }

    this.state = {
      locations: [],
      step,
      room: { ...defaultRoom },
      deleteRoom: null,
      alertMessage: '',
      errorDeleteRoom: false,
    };

    this.removeMessage = this.removeMessage.bind(this);
    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
  }

  componentDidMount() {
    const { roomActions, loadedOptions, tcEvent } = this.props;
    this.fetchData();

    if (!loadedOptions && !isEmpty(tcEvent)) {
      roomActions.getRoomOptions(tcEvent.testingEvent.adminId.cycleId.id);
    }
  }

  componentDidUpdate() {
    const { rooms, locations } = this.props;

    if (locations.length) {
      if (this.state.locations.length <= 0) {
        this.setState({
          locations: mapRooms(locations, rooms),
        });
      }
    }

    // if (room && !room.loading && params.roomId && isUndefined(this.state.room.id)) {
    //   this.setState({
    //     room,
    //   });
    // }
  }

  componentWillUnmount() {
    this.props.roomActions.clearPastEventsAction();
  }

  fetchData = async () => {
    const { match, staticActions, roomActions, tcEventActions, location } = this.props;

    // Extract roomId from pathname since it's in the wildcard portion
    // Path format: /test-event/:eventId/rooms/:step/:roomId?
    const pathParts = location.pathname.split('/');
    const roomsIndex = pathParts.indexOf('rooms');
    const roomId = pathParts[roomsIndex + 2]; // step is at +1, roomId at +2

    if (roomId && !Number.isNaN(roomId)) {
      await roomActions.getRoom(roomId);
      const { room } = this.props;
      this.setState({
        room: room.id && !room.loading ? room : { ...defaultRoom },
      });
    }

    tcEventActions.getTcEvent(match.params.eventId).then(() => {
      staticActions.getRoomTypes(match.params.eventId);
      tcEventActions.getTestCenterCycle(match.params.eventId);
      roomActions.getRoomOptions(this.props.tcEvent.testingEvent.adminId.cycleId.id);
    });

    tcEventActions.getRooms(match.params.eventId);
    tcEventActions.getRoomsDTO(match.params.eventId);
  };

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  async nextStep(info, flag) {
    const { testCenterCycle } = this.props;

    let next = flag ? 1 : this.state.step + 1;
    let nextStep = testCenterCycle.cycle.seatingChartRequiredFlag ? this.BUILD_STEPS[next] : null;
    const stateRoom = this.state.room;
    let room;

    if (info) {
      room =
        !isEmpty(stateRoom) && info.id && stateRoom.id && info.id !== stateRoom.id
          ? { ...info }
          : { ...stateRoom, ...info };
    } else {
      room = { ...defaultRoom };
    }

    const omitLastStep = room.status !== ROOM_STATUS.inactive && next === 2;

    if (!nextStep || omitLastStep) {
      //  last step
      //  save info and re-direct to room list
      const stepsBack = next;
      const roomSaved = await this.save(room);
      if (!roomSaved) {
        return;
      }
      this.setState({
        room: { ...defaultRoom },
      });

      // We need to listen for the popstate event because history.go() is asynchronous
      // and we want to replace the state after the navigation
      globalThis.addEventListener(
        'popstate',
        () => {
          this.props.history.replace(
            `/test-event/${this.props.match.params.eventId}/staff-and-rooms`,
            {
              successNotif: this.props.t(
                `${room.id ? 'roomBuilder.successEditRoom' : 'roomBuilder.successCreateRoom'}`
              ),
            }
          );
        },
        {
          once: true,
        }
      );
      this.props.history.go(-stepsBack);
      return;
    }

    this.setState({
      step: next,
      room,
    });
    this.props.history.push(
      getUrl(nextStep.url, this.props.match.params.eventId, next ? room.id : null)
    );
  }

  async save(room) {
    if (room.id) {
      await this.props.roomActions.updateRoom(room);
    } else {
      await this.props.roomActions.createRoom(room);
    }
    if (isEmpty(this.props.errorRoom)) {
      this.props.tcEventActions.getRooms(this.props.tcEvent.id);
      return true;
    }
    return false;
  }

  prevStep() {
    if (this.state.step > 0) {
      const prevStep = this.state.step - 1;
      const room = this.state.room;
      this.setState({
        step: prevStep,
        room,
      });
    }
    this.props.history.goBack();
    this.props.roomActions.resetErrorRoomAction();
  }

  BUILD_STEPS = [
    {
      url: '/information',
    },
    {
      url: '/structure',
    },
    {
      url: '/seating-details',
    },
  ];

  render() {
    return (
      <section className="room-builder">
        <div className="mt-3">
          <Routes>
            <Route path="information/:roomId?" element={<InformationView parent={this} />} />
            <Route path="structure/:roomId?" element={<StructureView parent={this} />} />
            <Route path="seating-details/:roomId?" element={<SeatingDetailsView parent={this} />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </div>
        <AlertMessage
          show={this.state.showMessage}
          removeMessage={this.removeMessage}
          message={this.state.alertMessage}
        />
      </section>
    );
  }
}

// Helper components to access route params and render views
const InformationView = withRouterLegacy(({ parent }) => {
  const {
    roomActions,
    loadedOptions,
    testCenterCycle,
    tcEvent,
    roomTypes,
    roomsDTO,
    roomTypesById,
    roomMaterials,
    roomOptions,
    materialChainActions,
    roomNameExistent,
    loadingRoomTypes,
    mappedRoomTiming,
    room: propsRoom,
  } = parent.props;
  const { locations, room } = parent.state;

  const roomDTO = find(roomsDTO, { id: room.id });

  return loadedOptions &&
    !!locations.length &&
    !loadingRoomTypes &&
    !propsRoom.loading &&
    !isEmpty(testCenterCycle) ? (
    <RoomInformation
      roomOptions={roomOptions}
      roomTypesById={roomTypesById}
      locations={locations}
      tc={testCenterCycle.testCenterProgram.testCenter}
      tcEvent={tcEvent}
      room={room}
      manualTimerFlag={roomDTO ? roomDTO.manualTimerFlag : false}
      roomTypes={roomTypes}
      countMaterials={roomMaterials.length}
      onContinue={parent.nextStep}
      checkRoomNameExistence={roomActions.checkRoomNameExistence}
      checkRoomMismatch={roomActions.checkRoomMismatch}
      roomNameExistent={roomNameExistent}
      onBack={parent.prevStep}
      getRoomMaterials={materialChainActions.getRoomMaterials}
      resetRoomMaterials={materialChainActions.resetRoomMaterials}
      mappedRoomTiming={mappedRoomTiming}
    />
  ) : (
    <Spinner />
  );
});

const StructureView = withRouterLegacy(({ parent }) => {
  const { loadedOptions, roomOptions, errorRoom } = parent.props;
  const { room } = parent.state;

  return (
    loadedOptions && (
      <RoomStructure
        roomOptions={roomOptions}
        room={room}
        errorRoom={errorRoom}
        onContinue={parent.nextStep}
        onBack={parent.prevStep}
      />
    )
  );
});

const SeatingDetailsView = withRouterLegacy(({ parent }) => {
  const { locations, errorRoom } = parent.props;
  const { room } = parent.state;

  return (
    <RoomSeatingDetails
      room={room}
      locations={locations}
      errorRoom={errorRoom}
      onContinue={parent.nextStep}
      onBack={parent.prevStep}
    />
  );
});

function mapStateToProps(state) {
  return {
    testCenterCycle: state.tcEvent.testCenterCycle || {},
    user: state.users.currentUser || {},
    tcEvent: state.tcEvent.currentEvent || {},
    rooms: state.tcEvent.rooms || [],
    loadingRooms: state.tcEvent.loadingRooms,
    roomTypes: state.statics.roomTypes,
    mappedRoomTiming: state.statics.mappedRoomTiming,
    loadingRoomTypes: state.statics.loadingRoomTypes,
    roomOptions: state.roomOptions.options,
    loadingOptions: state.roomOptions.loadingOptions,
    loadedOptions: state.roomOptions.loadedOptions,
    errorRoomOptions: state.roomOptions.errorOptions || {},
    room: state.roomOptions.room || {},
    errorRoom: state.roomOptions.errorRoom || {},
    locations: state.tcEvent.eventLocations || [],
    allLocations: state.tcEvent.locations || [],
    roomsDTO: state.tcEvent.roomsDTO,
    roomTypesById: mapRoomTypesById(state),
    roomMaterials: state.materialChain.roomMaterials,
    loadedPastEvents: state.roomOptions.loadedPastEvents,
    loadingPastEvents: state.roomOptions.loadingPastEvents,
    errorPastEvents: state.roomOptions.errorPastEvents,
    errorCopyRooms: state.roomOptions.errorCopyRooms,
    roomNameExistent: state.roomOptions.roomNameExistent,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    tcEventActions: bindActionCreators(tcEventActions, dispatch),
    roomActions: bindActionCreators(roomActions, dispatch),
    staticActions: bindActionCreators(staticActions, dispatch),
    materialChainActions: bindActionCreators(materialChainActions, dispatch),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(EventRoomsContainer))
);

EventRoomsContainer.propTypes = {
  rooms: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  room: PropTypes.shape({
    id: PropTypes.number,
    loading: PropTypes.bool,
  }).isRequired,
  tcEvent: PropTypes.shape({
    id: PropTypes.number,
    studyFlag: PropTypes.bool,
    testingEvent: PropTypes.shape({
      adminId: PropTypes.shape({
        cycleId: PropTypes.shape({
          id: PropTypes.number,
        }),
      }),
    }),
  }).isRequired,
  testCenterCycle: PropTypes.shape({
    cycle: PropTypes.shape({
      seatingChartRequiredFlag: PropTypes.bool,
    }),
    testCenterProgram: PropTypes.shape({
      testCenter: PropTypes.shape({}),
    }),
  }).isRequired,
  loadingRoomTypes: PropTypes.bool,
  roomTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  mappedRoomTiming: PropTypes.shape({}).isRequired,
  roomOptions: PropTypes.shape({}).isRequired,
  loadedOptions: PropTypes.bool,
  errorRoom: PropTypes.shape({}),
  roomsDTO: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      manualTimerFlag: PropTypes.bool,
    })
  ),
  roomTypesById: PropTypes.shape({}),
  roomMaterials: PropTypes.arrayOf(PropTypes.shape({})),
  roomNameExistent: PropTypes.bool,
  history: PropTypes.shape({
    go: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string,
      step: PropTypes.string,
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
  tcEventActions: PropTypes.shape({
    getTcEvent: PropTypes.func.isRequired,
    getRooms: PropTypes.func.isRequired,
    getTestCenterCycle: PropTypes.func.isRequired,
    getRoomsDTO: PropTypes.func.isRequired,
  }).isRequired,
  roomActions: PropTypes.shape({
    getRoomOptions: PropTypes.func.isRequired,
    getRoom: PropTypes.func.isRequired,
    createRoom: PropTypes.func.isRequired,
    updateRoom: PropTypes.func.isRequired,
    resetErrorRoomAction: PropTypes.func.isRequired,
    clearPastEventsAction: PropTypes.func.isRequired,
    checkRoomNameExistence: PropTypes.func.isRequired,
    checkRoomMismatch: PropTypes.func.isRequired,
  }).isRequired,
  staticActions: PropTypes.shape({
    getRoomTypes: PropTypes.func.isRequired,
  }).isRequired,
  materialChainActions: PropTypes.shape({
    getRoomMaterials: PropTypes.func.isRequired,
    resetRoomMaterials: PropTypes.func.isRequired,
  }).isRequired,
};
